/** English. */
export default {
  header: {
    features: 'Features',
    pricing: 'Pricing',
    contact: 'Contact',
    getStarted: 'Get started',
    login: 'Log in',
  },

  footer: {
    description:
      'A platform for members, venue rental, events and accounting, made for associations.',
    product: 'Product',
    help: 'Help',
    legal: 'Legal',
    features: 'Features',
    pricing: 'Pricing',
    getStarted: 'Get started',
    contactUs: 'Contact us',
    privacy: 'Privacy',
    terms: 'Terms',
    copyright: 'All rights reserved.',
  },

  home: {
    unknownTenant:
      'No active organization found at {host}. If the organization was just created it can take a few minutes before the site is ready - try reloading shortly. If you are unsure about the address, or the problem persists, please',
    unknownTenantHere: 'this address',
    unknownTenantContact: 'contact us',
    heroTitlePre: 'Digitize your organization with',
    heroSubtitle:
      'A platform for members, venue rental, events and accounting, made for associations.',
    ctaFree: 'Get started for free',
    learnMore: 'Learn more',
    featuresTitle: 'What you need to run your association',
    featuresSubtitle: 'The tools together in one platform',
    features: [
      { icon: '👥', title: 'Member register', desc: 'Members, memberships and households, with an invoice when a membership renews.' },
      { icon: '🏠', title: 'Venue rental', desc: 'Bookings with prices, contract, deposit and invoice.' },
      { icon: '📅', title: 'Events', desc: 'Ticket sales with capacity, and QR code at the door.' },
      { icon: '💳', title: 'Invoices and payment', desc: 'Invoices and credit notes, and payment by card and Vipps via Nets.' },
      { icon: '📧', title: 'Email and SMS', desc: 'Confirmations, reminders, invoices and tickets are sent automatically.' },
      { icon: '🌐', title: 'Own website', desc: 'Your association’s own site with pages, news and its own look.' },
    ],
    stepsTitle: 'How to get started',
    steps: [
      { num: 1, title: 'Send a request', desc: 'Tell us about your association and we set up your site.' },
      { num: 2, title: 'Set up', desc: 'Add venues, membership types and prices.' },
      { num: 3, title: 'Start using it', desc: 'Take bookings, sell tickets and send invoices.' },
    ],
    ctaTitle: 'Ready to simplify running your organization?',
    ctaText: 'Start for free today. No lock-in.',
    ctaButton: 'Get started for free',
    heroImageAlt: 'The overview in the administration of a demo association',
  },

  features: {
    heroTitle: 'Powerful features for your organization',
    heroSubtitle: 'Members, venue rental, events, invoicing and accounting in one platform.',
    list: [
      {
        icon: '👥',
        title: 'Member administration',
        description: 'Members, memberships and households in one place.',
        details: [
          'Membership types and households with children',
          'Renewal with invoice',
          'Volunteer work and hours',
        ],
      },
      {
        icon: '🏠',
        title: 'Venue rental',
        description: 'Receive and follow up bookings.',
        details: ['Prices, add-ons and public holidays', 'Contract signed online', 'Deposit and invoice'],
      },
      {
        icon: '📅',
        title: 'Events',
        description: 'Sell tickets and let the right people in.',
        details: [
          'Ticket sales with capacity',
          'Ticket types in sequence, such as early bird and door sales',
          'QR code at the door',
        ],
      },
      {
        icon: '💳',
        title: 'Invoices and payment',
        description: 'Invoices and credit notes, and card payment.',
        details: ['Invoices with reminders', 'Card and Vipps via Nets', 'Accounting and chart of accounts'],
      },
      {
        icon: '📧',
        title: 'Communication',
        description: 'Messages are sent automatically at the right time.',
        details: ['Email and SMS', 'Templates in several languages', 'Notifications at the right time'],
      },
      {
        icon: '🌐',
        title: 'Own website',
        description: 'Your association’s own site with pages and news.',
        details: ['Pages and news', 'Three looks and your own colours', 'Own domain (set up by us)'],
      },
      {
        icon: '🔐',
        title: 'Security and privacy',
        description: 'Each association is kept apart from the others.',
        details: [
          'Two-factor sign-in',
          'Data is stored in Norway',
          'Access, export and deletion of personal data',
        ],
      },
    ],
    ctaTitle: 'Ready to get started?',
    ctaText: 'Start for free today and experience how easy running an organization can be.',
    ctaButton: 'Get started for free',
    screensTitle: 'What it looks like',
    screensSubtitle:
      'Screenshots from a demo association with invented data. The administration is in Norwegian.',
    screens: [
      { title: 'Overview', text: 'What needs following up, and what is coming, on one page.' },
      { title: 'Bookings', text: 'Requests, confirmations and the status of every booking.' },
      { title: 'Calendar', text: 'Visitors see when the venues are free and what is happening in the house.' },
      { title: 'Tickets', text: 'Sales per ticket type, attendees and check-in.' },
      { title: 'Choose a seat', text: 'The buyer chooses their seats on the map of the venue.' },
      { title: 'Seating plans', text: 'Drag tables, chairs, stage and bar onto the map, with the floor plan behind.' },
      { title: 'Memberships', text: 'Members, membership types and households.' },
      { title: 'Invoices', text: 'Invoices and credit notes with status, due date and outstanding amount.' },
      { title: 'On the phone', text: 'The association’s website works on a phone.' },
    ],
  },

  pricing: {
    heroTitle: 'Simple, transparent pricing',
    heroSubtitle: 'Choose the plan that suits your association.',
    loading: 'Loading prices...',
    loadError: 'Could not fetch updated prices. Showing cached prices.',
    popular: 'Most popular',
    perMonth: 'kr/mo',
    unlimited: 'Unlimited',
    unlimitedMembers: 'Unlimited members',
    upToMembers: 'Up to {count} members',
    prioritySupport: 'Priority support: we start within 30 minutes, weekdays 09–15',
    planDescFree: 'For small organizations',
    planDescStandard: 'For active organizations',
    planDescAdvanced: 'For large organizations',
    ctaFree: 'Get started for free',
    ctaTrial: 'Send a request',
    faqTitle: 'Frequently asked questions',
    faq: [
      { q: 'Can I switch plans at any time?', a: 'Yes, get in touch and we switch the plan.' },
      { q: 'What happens if we exceed the member limit?', a: 'We get in touch and agree on an upgrade. Nothing is shut off without us talking to you first.' },
      { q: 'Is there a lock-in period?', a: 'No, all plans can be cancelled at any time.' },
      { q: 'Do you offer discounts for non-profits?', a: 'Yes, contact us for special pricing for non-profit organizations.' },
      { q: 'What does card payment cost?', a: 'Card and Vipps go through Nets. The association has its own agreement with Nets and pays the fee directly to them.' },
    ],
    emailSms: 'Email and SMS',
    bookingsPerMonth: '{count} bookings a month',
    unlimitedBookings: 'Unlimited bookings',
    ticketsPerMonth: '{count} tickets a month',
    unlimitedTickets: 'Unlimited tickets',
    locations: 'Up to {count} venues',
    unlimitedLocations: 'Unlimited venues',
    storage: '{size} storage',
    customBranding: 'Own look and colours',
    exportCsv: 'Export to CSV',
    introNotice:
      'During an introductory period all plans are free. We give at least 30 days’ notice before billing starts.',
  },

  contact: {
    heroTitle: 'Contact us',
    heroSubtitle: 'We are here to help. Write to us and we will reply as soon as we can.',
    formTitle: 'Write to us',
    formIntro:
      'Do you have questions about CommunityNord, prices, or how we can help your association? Fill in the form, or send us an email.',
    email: 'Email',
    name: 'Name',
    organization: 'Organization',
    subject: 'Subject',
    chooseSubject: 'Choose subject',
    subjectGeneral: 'General question',
    subjectPricing: 'Pricing and subscription',
    subjectSupport: 'Technical support',
    subjectDemo: 'Request a demo',
    subjectPartnership: 'Partnership',
    message: 'Message',
    send: 'Send message',
    success:
      'Thank you! We have received your message and will get back to you as soon as we can.',
    error: 'Something went wrong. Try again, or send us an email.',
    tooMany: 'You have sent many messages. Try again later, or send us an email.',
    sending: 'Sending...',
    supportNote:
      'Standard and Advanced have priority support: we start on your case within 30 minutes, weekdays 09–15.',
  },

  login: {
    title: 'Sign in',
    subtitle:
      'Each association has its own sign-in. Enter the association’s address and we take you there.',
    label: 'Association address',
    placeholder: 'yourassociation',
    button: 'Go to sign-in',
    hint:
      'If the association has its own domain, use that instead. If you cannot find the address, ask the board or contact us.',
    invalid: 'Use only lowercase letters, digits and hyphens.',
  },

  signup: {
    heroTitle: 'Get started with CommunityNord',
    heroSubtitle:
      'Fill in the form to create a new organization. We will email you when your account is ready.',
    formTitle: 'Create new organization',
    success:
      'Thank you! We have received your request and will contact you as soon as your account is ready.',
    error: 'Something went wrong while submitting. Please try again or contact us.',
    orgName: 'Organization name',
    orgNamePlaceholder: 'Name of your organization',
    contactPerson: 'Contact person',
    contactPersonPlaceholder: 'Your full name',
    email: 'Email',
    phone: 'Phone',
    plan: 'Preferred plan',
    messageOptional: 'Message (optional)',
    messagePlaceholder: 'Tell us a bit about your organization...',
    sending: 'Sending...',
    submit: 'Send request',
    free: 'Free',
    perMonth: 'kr/mo',
    upTo50: 'Up to 50 members',
    upTo500: 'Up to 500 members',
    unlimitedMembers: 'Unlimited members',
    nextTitle: 'What happens next?',
    nextSteps: [
      { title: 'Request sent', desc: 'We receive your request and send a confirmation to your email.' },
      { title: 'Processing', desc: 'Our team usually processes your request within 1-2 business days.' },
      { title: 'Welcome', desc: 'You receive an invitation link by email to create your account.' },
    ],
    questionsTitle: 'Questions?',
    questionsContact: 'Contact us at',
  },

  onboarding: {
    homeTitle: 'CommunityNord Onboarding',
    homeSubtitle:
      'To create a new organization you need an invitation link. Contact an administrator to receive one.',
    homeGotLink: 'Received a link?',
    homeGotLinkText: 'Click the link you received by email to start setting up your organization.',
    homeBack: '← Back to communitynord.com',
    validating: 'Validating the invitation...',
    invalidTitle: 'Invalid invitation',
    invalidContact: 'Contact the administrator to get a new invitation link.',
    connectError: 'Could not reach the server. Please try again later.',
    deviceBound:
      'This link has already been opened on another device and is tied to it. Open the link on the first device to continue, or write to support@communitynord.com and we will send a new one.',
    genericError: 'Something went wrong. Please try again.',
    steps: ['Welcome', 'Organization', 'Contact', 'Account', 'Confirm'],
    step1Title: 'Welcome to CommunityNord',
    step1Subtitle:
      'You have been invited to create a new organization. We will guide you through the setup step by step.',
    step1Suggested: 'Suggested name:',
    step1SentTo: 'Invitation sent to:',
    step1Expires: 'The invitation expires:',
    step1Needs: 'You will need:',
    step1NeedsList: [
      'The name of the organization',
      'Contact information',
      'Organization number, if any',
      'Preferred username and password',
    ],
    step1Start: 'Get started →',
    back: '← Back',
    next: 'Next →',
    step2Title: 'About the organization',
    step2Subtitle: 'Tell us about the organization that will use the platform.',
    orgName: 'Organization name',
    orgNamePlaceholder: 'E.g. Bergen Youth Club',
    orgNameRequired: 'Organization name is required',
    slug: 'URL prefix (slug)',
    slugRequired: 'URL prefix is required',
    slugFormat: 'Only lowercase letters, numbers and hyphens',
    slugMin: 'At least 2 characters',
    shortName: 'Short name',
    shortNameRequired: 'Short name is required',
    shortNameMax: 'Max 10 characters',
    shortNameFormat: 'Only letters and numbers',
    shortNameHint: 'Used as a prefix in invoice numbers etc.',
    customDomain: 'Custom domain',
    customDomainPlaceholder: 'e.g. example.org',
    customDomainHint: 'Optional. Lets you use your own domain in addition to the subdomain.',
    customDomainNoProtocol: 'Enter only the domain, without http:// or https://',
    customDomainNoPath: 'Enter only the domain, without a path',
    customDomainInvalid: 'Invalid domain format',
    country: 'Country',
    countryHint: 'The country your organization belongs to',
    countryNames: { NO: 'Norway', SE: 'Sweden', DK: 'Denmark' },
    orgNumber: 'Organization number',
    orgNumberHint: 'Optional. Company/organization registry number.',
    appearance: 'Appearance',
    layout: 'Site layout',
    layoutHint: 'Can be changed at any time under Admin → Branding.',
    layouts: [
      { key: 'classic', name: 'Classic', description: 'Traditional layout with centered content and logo on the left. Fits most.' },
      { key: 'modern', name: 'Modern', description: 'Full-width hero and compact sticky header. Fresh and airy.' },
      { key: 'compact', name: 'Compact', description: 'Minimal spacing and dense information. Great for content-heavy sites.' },
      { key: 'custom', name: 'Custom', description: 'A bespoke design set up together with us. We will get in touch.' },
    ],
    tagline: 'Tagline / motto',
    taglinePlaceholder: 'E.g. "Together we create activity"',
    taglineHint: 'Optional. Shown in the site footer.',
    logo: 'Logo',
    logoUpload: 'Upload logo',
    logoChange: 'Change image',
    logoRemove: 'Remove',
    logoHint: 'Optional. PNG/JPG/SVG, max 2 MB. Can also be added later.',
    logoTooBig: 'The image is too large (max 2 MB)',
    logoNotImage: 'The file must be an image',
    logoUseUrl: 'Use a URL instead',
    logoUrlPlaceholder: 'https://...',
    logoUrlHint: 'Absolute URL to the logo (https://...).',
    subscriptionPlan: 'Subscription plan',
    subscriptionPlanHint:
      'This becomes the starting plan for the tenant and can be changed in the portal later.',
    step3Title: 'Contact information',
    step3Subtitle:
      'Who is the main contact for the organization? This person becomes the account owner.',
    contactPerson: 'Contact person',
    contactPersonRequired: 'Contact person is required',
    email: 'Email address',
    emailRequired: 'Email is required',
    emailInvalid: 'Invalid email address',
    phone: 'Phone number',
    postalAddress: 'Postal address',
    optional: '(optional)',
    streetAddress: 'Street address',
    city: 'City',
    postalCode: 'Postal code',
    sameAsPostal: 'Visiting address is the same as the postal address',
    visitingAddress: 'Visiting address',
    step4Title: 'Create user account',
    step4Subtitle:
      'This user becomes the owner of the organization with full access. You can add more users later.',
    username: 'Username',
    usernameRequired: 'Username is required',
    usernameMin: 'At least 3 characters',
    usernameMax: 'Max 256 characters',
    usernameEmailInvalid: 'Invalid email format',
    usernameFormat: 'Use username format (a-z, 0-9, dot, underscore, hyphen) or a valid email address',
    usernamePlaceholder: 'john.smith or name@domain.org',
    usernameHint: 'Used to log in. Can be a username or an email address.',
    loginEmail: 'Email (for login)',
    loginEmailHint: 'Taken from the contact information in the previous step.',
    password: 'Password',
    passwordRequired: 'Password is required',
    passwordMin: 'At least 8 characters',
    passwordShow: 'Show',
    passwordHide: 'Hide',
    passwordHint: 'Tip: Use a mix of upper/lowercase letters, numbers and symbols.',
    passwordConfirm: 'Confirm password',
    passwordConfirmPlaceholder: 'Repeat the password',
    passwordMismatch: 'The passwords do not match',
    step5Title: 'Confirm and create',
    step5Subtitle:
      'Review the information before creating the organization. You can go back to make changes.',
    reviewOrg: 'Organization',
    reviewName: 'Name',
    reviewUrl: 'URL',
    reviewCustomDomain: 'Custom domain',
    reviewCountry: 'Country',
    reviewShortName: 'Short name',
    reviewOrgNumber: 'Org. no.',
    reviewPlan: 'Plan',
    reviewLayout: 'Layout',
    reviewTagline: 'Tagline',
    reviewLogo: 'Logo',
    reviewContact: 'Contact',
    reviewContactPerson: 'Contact person',
    reviewEmail: 'Email',
    reviewPhone: 'Phone',
    reviewPostal: 'Postal address',
    reviewVisiting: 'Visiting address',
    reviewAddress: 'Address',
    reviewPlace: 'Place',
    reviewAccount: 'User account',
    reviewUsername: 'Username',
    reviewPassword: 'Password',
    reviewRole: 'Role',
    reviewOwner: 'Owner',
    creating: 'Creating...',
    create: 'Create organization ✓',
    successTitle: '{name} has been created!',
    successAwaiting:
      'We are now preparing your site — this is a manual step where we verify that everything is set up correctly. You will receive an email at {email} as soon as the site is ready to use.',
    successAwaitingNoEmail:
      'We are now preparing your site — this is a manual step where we verify that everything is set up correctly. You will receive an email as soon as the site is ready to use.',
    successWhatNow: 'What happens now?',
    successStep1: 'We verify the setup and make the site ready. This usually takes a short while.',
    successStep2: 'You receive an email when the site is ready, with a login link.',
    successStep3: 'Log in with the username {username} and the password you chose.',
    successDetails: 'Your details',
    successOrg: 'Organization',
    successUrl: 'Web address',
    successUsername: 'Username',
    successDnsTitle: 'Custom domain: {domain}',
    successDnsIntro:
      'For {domain} to point to your site, the DNS settings at your domain provider must be updated. Choose one of the options:',
    successDnsCnameTitle: 'Option 1 — CNAME (easiest)',
    successDnsCnameText: 'Create a CNAME record pointing to your subdomain:',
    successDnsATitle: 'Option 2 — A record',
    successDnsAText: 'Point the domain directly to our server IP:',
    successDnsNote:
      'DNS changes can take up to a few hours to take effect. Note: a CNAME cannot be used on a root domain (example.org) with all providers — use an A record instead, or a CNAME on www. We verify the domain as part of preparing your site.',
    successDnsType: 'Type',
    successDnsName: 'Name/host',
    successDnsValue: 'Value/target',
  },
}
